/*
 * -------------------------------------------------------------------------------------------------------------------
 * Copyright (c) 2025 Christian Bannard
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 * 
 *     http://www.apache.org/licenses/LICENSE-2.0
 * 
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 * -------------------------------------------------------------------------------------------------------------------
 * File: c:\localdev\templaredge\frontend\src\pages\Tasks\TaskList.tsx
 *  
 * Author: Christian Bannard
 * Created: 2025-11-23
 *  
 * Description: Task management list view with filtering and search capabilities
 * -------------------------------------------------------------------------------------------------------------------
 * HISTORY:
 * Date      	By	Comments
 * ----------	---	-------------------------------------------------------------------------------------------------
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiCall, ENDPOINTS } from '../../config/api';
import { useAuth } from '../../contexts/AuthContext';
import { Task, Patient } from '../../types/task';
import { FiPlus, FiSearch, FiX, FiClock, FiUser, FiFileText, FiPaperclip } from 'react-icons/fi';
import TaskView from './TaskView';

interface TaskItemProps {
	task: Task;
	onClick: () => void;
}

const TaskItem: React.FC<TaskItemProps> = ({ task, onClick }) => {
	const formatDate = (dateString: string) => {
		const date = new Date(dateString);
		return date.toLocaleDateString('en-AU', {
			day: '2-digit',
			month: '2-digit',
			year: 'numeric'
		});
	};

	const getPriorityColor = (priority: string) => {
		switch (priority) {
			case 'urgent': return 'badge-error';
			case 'high': return 'badge-warning';
			case 'medium': return 'badge-info';
			case 'low': return 'badge-ghost';
			default: return 'badge-ghost';
		}
	};

	const getStatusColor = (status: string) => {
		switch (status) {
			case 'completed': return 'badge-success';
			case 'in_progress': return 'badge-primary';
			case 'open': return 'badge-info';
			case 'cancelled': return 'badge-ghost';
			default: return 'badge-ghost';
		}
	};

	const isOverdue = task.due_date && new Date(task.due_date) < new Date() && task.status !== 'completed';

	return (
		<div
			onClick={onClick}
			className="card bg-base-100 shadow-sm hover:shadow-md transition-shadow cursor-pointer border border-base-300"
		>
			<div className="card-body p-4">
				<div className="flex justify-between items-start mb-2">
					<h3 className="card-title text-base">{task.title}</h3>
					<div className="flex gap-2">
						<span className={`badge badge-sm ${getPriorityColor(task.priority)}`}>
							{task.priority}
						</span>
						<span className={`badge badge-sm ${getStatusColor(task.status)}`}>
							{task.status.replace('_', ' ')}
						</span>
					</div>
				</div>

				{task.description && (
					<p className="text-sm opacity-70 line-clamp-2 mb-2">
						{task.description}
					</p>
				)}

				<div className="flex flex-wrap gap-3 text-xs opacity-70">
					{task.patient_name && (
						<div className="flex items-center gap-1">
							<FiUser className="w-3 h-3" />
							<span>{task.patient_name}</span>
						</div>
					)}
					
					{task.assigned_to_name && (
						<div className="flex items-center gap-1">
							<FiUser className="w-3 h-3" />
							<span>Assigned to: {task.assigned_to_name}</span>
						</div>
					)}

					{task.due_date && (
						<div className={`flex items-center gap-1 ${isOverdue ? 'text-error font-semibold' : ''}`}>
							<FiClock className="w-3 h-3" />
							<span>Due: {formatDate(task.due_date)}</span>
						</div>
					)}

					{(task.notes_count ?? 0) > 0 && (
						<div className="flex items-center gap-1">
							<FiFileText className="w-3 h-3" />
							<span>{task.notes_count} note{task.notes_count !== 1 ? 's' : ''}</span>
						</div>
					)}

					{(task.attachments_count ?? 0) > 0 && (
						<div className="flex items-center gap-1">
							<FiPaperclip className="w-3 h-3" />
							<span>{task.attachments_count} attachment{task.attachments_count !== 1 ? 's' : ''}</span>
						</div>
					)}
				</div>

				<div className="text-xs opacity-50 mt-2">
					Created by {task.created_by_name || 'Unknown'} on {formatDate(task.created_at)}
				</div>
			</div>
		</div>
	);
};

const TaskList: React.FC = () => {
	const { token, user } = useAuth();
	const navigate = useNavigate();
	const [tasks, setTasks] = useState<Task[]>([]);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);

	// Filters
	const [statusFilter, setStatusFilter] = useState<string>('all');
	const [priorityFilter, setPriorityFilter] = useState<string>('all');
	const [assignmentFilter, setAssignmentFilter] = useState<string>('all');
	const [searchTerm, setSearchTerm] = useState('');

	// Patient search
	const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null);
	const [patientSearch, setPatientSearch] = useState('');
	const [patientResults, setPatientResults] = useState<Patient[]>([]);
	const [showPatientResults, setShowPatientResults] = useState(false);

	// Selected task for viewing
	const [selectedTask, setSelectedTask] = useState<Task | null>(null);
	const [showTaskView, setShowTaskView] = useState(false);

	// Patient search with debounce
	useEffect(() => {
		if (patientSearch.length >= 2 && token && !selectedPatient) {
			const timer = setTimeout(async () => {
				try {
					const response = await apiCall(
						`${ENDPOINTS.PATIENTS.PATIENTS}?search=${encodeURIComponent(patientSearch)}`,
						{ method: 'GET' },
						token
					);

					if (response.ok) {
						const data = await response.json();
						const patients = data.results || data || [];

						const searchLower = patientSearch.toLowerCase().trim();
						const filtered = patients.filter((patient: Patient) => {
							const fullName = `${patient.first_name} ${patient.last_name}`.toLowerCase();
							const reverseName = `${patient.last_name} ${patient.first_name}`.toLowerCase();
							const mrn = (patient.medical_record_number || '').toLowerCase();

							return fullName.includes(searchLower) ||
								reverseName.includes(searchLower) ||
								mrn.includes(searchLower);
						});

						setPatientResults(filtered);
						setShowPatientResults(filtered.length > 0);
					}
				} catch (error) {
					console.error('Error searching patients:', error);
				}
			}, 300);

			return () => clearTimeout(timer);
		} else if (selectedPatient) {
			setShowPatientResults(false);
		} else {
			setPatientResults([]);
			setShowPatientResults(false);
		}
	}, [patientSearch, token, selectedPatient]);

	// Fetch tasks
	useEffect(() => {
		fetchTasks();
	}, [token, statusFilter, priorityFilter, assignmentFilter, selectedPatient, searchTerm]);

	const fetchTasks = async () => {
		if (!token) return;

		setLoading(true);
		setError(null);

		try {
			const params = new URLSearchParams();

			if (statusFilter !== 'all') {
				params.append('status', statusFilter);
			}

			if (priorityFilter !== 'all') {
				params.append('priority', priorityFilter);
			}

			if (assignmentFilter === 'assigned_to_me' && user?.id) {
				params.append('assigned_to_id', user.id.toString());
			} else if (assignmentFilter === 'created_by_me' && user?.id) {
				params.append('created_by_id', user.id.toString());
			}

			if (selectedPatient) {
				params.append('patient_id', selectedPatient.id.toString());
			}

			if (searchTerm.trim()) {
				params.append('search', searchTerm.trim());
			}

			const url = `${ENDPOINTS.TASKS.LIST}?${params.toString()}`;
			const response = await apiCall(url, { method: 'GET' }, token);

			if (!response.ok) {
				throw new Error('Failed to fetch tasks');
			}

			const data = await response.json();
			const tasksList = data.results || data || [];
			setTasks(Array.isArray(tasksList) ? tasksList : []);
		} catch (err) {
			console.error('Error fetching tasks:', err);
			setError('Failed to load tasks');
		} finally {
			setLoading(false);
		}
	};

	const handlePatientSelect = (patient: Patient) => {
		setSelectedPatient(patient);
		setPatientSearch(`${patient.first_name} ${patient.last_name}`);
		setShowPatientResults(false);
		setPatientResults([]);
	};

	const clearPatientFilter = () => {
		setSelectedPatient(null);
		setPatientSearch('');
		setPatientResults([]);
		setShowPatientResults(false);
	};

	const handleTaskClick = (task: Task) => {
		setSelectedTask(task);
		setShowTaskView(true);
	};

	const handleCloseTaskView = () => {
		setShowTaskView(false);
		setSelectedTask(null);
		fetchTasks(); // Refresh tasks after viewing/editing
	};

	const handleCreateTask = () => {
		navigate('/tasks/new', { state: { patient: selectedPatient } });
	};

	return (
		<div className="h-full flex flex-col">
			{/* Header */}
			<div className="bg-base-200 p-6 shadow-lg flex-shrink-0">
				<div className="flex justify-between items-center mb-4">
					<h1 className="text-2xl font-bold">Tasks</h1>
					<button onClick={handleCreateTask} className="btn btn-primary gap-2">
						<FiPlus className="w-4 h-4" />
						Create Task
					</button>
				</div>

				{/* Filters */}
				<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
					{/* Patient Search */}
					<div className="form-control relative">
						<label className="label">
							<span className="label-text">Patient</span>
						</label>
						{selectedPatient ? (
							<div className="inline-flex items-center space-x-2 px-3 py-2 bg-primary text-primary-content border border-primary rounded">
								<span>
									{selectedPatient.first_name} {selectedPatient.last_name}
								</span>
								<button
									onClick={clearPatientFilter}
									className="opacity-70 hover:opacity-100"
									title="Clear patient filter"
								>
									<FiX className="w-4 h-4" />
								</button>
							</div>
						) : (
							<input
								type="text"
								value={patientSearch}
								onChange={(e) => setPatientSearch(e.target.value)}
								className="input input-bordered w-full"
								placeholder="Search patient..."
							/>
						)}

						{showPatientResults && patientResults.length > 0 && (
							<div className="absolute top-full left-0 mt-1 w-full bg-base-100 rounded-lg shadow-xl border border-base-300 max-h-60 overflow-y-auto z-50">
								{patientResults.map((patient) => (
									<div
										key={patient.id}
										onClick={() => handlePatientSelect(patient)}
										className="px-3 py-2 hover:bg-base-200 cursor-pointer border-b border-base-300 last:border-b-0"
									>
										<div className="font-medium">
											{patient.first_name} {patient.last_name}
										</div>
										<div className="text-xs opacity-60">
											DOB: {patient.date_of_birth || 'N/A'} | MRN: {patient.medical_record_number || 'N/A'}
										</div>
									</div>
								))}
							</div>
						)}
					</div>

					{/* Status Filter */}
					<div className="form-control">
						<label className="label">
							<span className="label-text">Status</span>
						</label>
						<select
							value={statusFilter}
							onChange={(e) => setStatusFilter(e.target.value)}
							className="select select-bordered w-full"
						>
							<option value="all">All Statuses</option>
							<option value="open">Open</option>
							<option value="in_progress">In Progress</option>
							<option value="completed">Completed</option>
							<option value="cancelled">Cancelled</option>
						</select>
					</div>

					{/* Priority Filter */}
					<div className="form-control">
						<label className="label">
							<span className="label-text">Priority</span>
						</label>
						<select
							value={priorityFilter}
							onChange={(e) => setPriorityFilter(e.target.value)}
							className="select select-bordered w-full"
						>
							<option value="all">All Priorities</option>
							<option value="urgent">Urgent</option>
							<option value="high">High</option>
							<option value="medium">Medium</option>
							<option value="low">Low</option>
						</select>
					</div>

					{/* Assignment Filter */}
					<div className="form-control">
						<label className="label">
							<span className="label-text">Assignment</span>
						</label>
						<select
							value={assignmentFilter}
							onChange={(e) => setAssignmentFilter(e.target.value)}
							className="select select-bordered w-full"
						>
							<option value="all">All Tasks</option>
							<option value="assigned_to_me">Assigned to Me</option>
							<option value="created_by_me">Created by Me</option>
						</select>
					</div>
				</div>

				{/* Search */}
				<div className="form-control mt-4">
					<div className="input-group">
						<span className="bg-base-300">
							<FiSearch className="w-5 h-5" />
						</span>
						<input
							type="text"
							value={searchTerm}
							onChange={(e) => setSearchTerm(e.target.value)}
							className="input input-bordered flex-1"
							placeholder="Search tasks by title or description..."
						/>
						{searchTerm && (
							<button
								onClick={() => setSearchTerm('')}
								className="btn btn-square btn-ghost"
							>
								<FiX className="w-5 h-5" />
							</button>
						)}
					</div>
				</div>
			</div>

			{/* Task List */}
			<div className="flex-1 overflow-y-auto p-6">
				{loading ? (
					<div className="flex justify-center items-center h-64">
						<span className="loading loading-spinner loading-lg"></span>
					</div>
				) : error ? (
					<div className="alert alert-error">
						<span>{error}</span>
					</div>
				) : tasks.length === 0 ? (
					<div className="text-center py-12 opacity-60">
						<p className="text-lg mb-2">No tasks found</p>
						<p className="text-sm">Create a new task to get started</p>
					</div>
				) : (
					<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
						{tasks.map((task) => (
							<TaskItem key={task.id} task={task} onClick={() => handleTaskClick(task)} />
						))}
					</div>
				)}
			</div>

			{/* Task View Modal */}
			{showTaskView && selectedTask && (
				<TaskView
					taskId={selectedTask.id}
					onClose={handleCloseTaskView}
				/>
			)}
		</div>
	);
};

export default TaskList;


